import { useUser } from '@src/hooks';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import { exportOMSOrders } from '@src/utils/blotterQueries';
import {
  formattedDateForSubscription,
  getTypedKeys,
  orderStatusFilterTextToBackendStatus,
  parseDate,
} from '@talos/kyoko';
import { compact, isEmpty, pick } from 'lodash-es';
import { useCallback } from 'react';
import type { BlotterTableOrderFilter } from './useOrderFilter';
type UseHandleExportOrdersProps = {
  filter: BlotterTableOrderFilter;
};

export function useHandleExportOrders({ filter }: UseHandleExportOrdersProps) {
  const user = useUser();
  const { subAccountsByName } = useSubAccounts();

  return useCallback(() => {
    const serverFilter = onlyServerFilterKeys(filter) || {};
    const stringFilters = getTypedKeys(serverFilter)
      .map(key => {
        const value = serverFilter[key];
        if (isEmpty(value)) {
          return undefined;
        }
        switch (key as string) {
          case 'StartDate':
          case 'EndDate':
            return undefined;
          case 'Statuses':
            return `OrdStatus:${value}`;
          case 'Users':
            return `User:${value}`;
          case 'Symbols':
            return `Symbol:${value}`;
          case 'SubAccounts':
            // Map sub accont Names to their IDs to make this API happy
            return `SubAccountID:${compact(
              (value as string[]).map(subAcctName => subAccountsByName?.get(subAcctName)?.SubaccountID)
            )}`;
        }
        return `${key}:${value}`;
      })
      .compact();

    exportOMSOrders(user.OrgApiEndpoint, {
      startDate: formattedDateForSubscription(serverFilter.StartDate),
      endDate: formattedDateForSubscription(serverFilter.EndDate || parseDate(null)),
      orderBy: '-SubmitTime',
      filter: stringFilters.length > 0 ? stringFilters : undefined,
    });
  }, [filter, subAccountsByName, user.OrgApiEndpoint]);
}

function onlyServerFilterKeys(filter: BlotterTableOrderFilter | undefined) {
  if (!filter) {
    return filter;
  }
  const serverFilter = pick(filter, ['StartDate', 'EndDate', 'Statuses', 'SubAccounts', 'Symbols', 'HideApiCalls']);
  if (filter._statuses) {
    serverFilter.Statuses = orderStatusFilterTextToBackendStatus(filter._statuses);
  }
  return serverFilter;
}
